/**
 * Report Progress Tracking Types and Constants
 * 
 * Provides granular progress tracking for AI report generation,
 * giving users better visibility into what's happening during the
 * multi-minute report generation process.
 */

/**
 * Progress step identifiers - ordered by execution sequence
 */
export type ReportProgressStep =
  | 'initializing'           // Report created, starting generation
  | 'resolving_location'     // Determining geographic context
  | 'validating_domain'      // Checking domain format and accessibility
  | 'checking_robots'        // Validating robots.txt allows crawling
  | 'fetching_brand_assets'  // Getting brand data from Brandfetch
  | 'crawling_website'       // Crawling pages for brand snapshot
  | 'generating_snapshot'    // LLM generating brand snapshot from crawled content
  | 'querying_llms'          // Querying AI platforms for brand consistency
  | 'scoring_consistency'    // Calculating brand consistency scores
  | 'analyzing_competitors'  // Identifying and analyzing competitors
  | 'analyzing_technical'    // Running technical SEO analysis
  | 'generating_queries'     // Creating discovery queries
  | 'running_discovery'      // Executing discovery queries across platforms
  | 'fetching_serp_rankings' // Getting Google/Brave search rankings
  | 'calculating_sov'        // Computing share of voice metrics
  | 'generating_recommendations' // Creating actionable recommendations
  | 'finalizing'             // Final processing and cleanup
  | 'complete';              // Report generation finished

/**
 * Progress step configuration with display information
 */
export interface ProgressStepConfig {
  step: ReportProgressStep;
  percent: number;           // Estimated completion percentage at this step
  message: string;           // User-friendly status message
  estimatedSeconds: number;  // Estimated time this step takes
}

/**
 * Ordered progress steps with percentages and messages
 * Percentages are cumulative estimates based on typical execution times
 */
export const PROGRESS_STEPS: ProgressStepConfig[] = [
  { step: 'initializing', percent: 0, message: 'Starting report generation...', estimatedSeconds: 2 },
  { step: 'resolving_location', percent: 2, message: 'Resolving your location...', estimatedSeconds: 1 },
  { step: 'validating_domain', percent: 3, message: 'Validating domain...', estimatedSeconds: 2 },
  { step: 'checking_robots', percent: 5, message: 'Checking site accessibility...', estimatedSeconds: 3 },
  { step: 'fetching_brand_assets', percent: 8, message: 'Fetching brand assets...', estimatedSeconds: 5 },
  { step: 'crawling_website', percent: 12, message: 'Crawling your website...', estimatedSeconds: 15 },
  { step: 'generating_snapshot', percent: 20, message: 'Analyzing your brand identity...', estimatedSeconds: 20 },
  { step: 'querying_llms', percent: 30, message: 'Querying AI platforms...', estimatedSeconds: 45 },
  { step: 'scoring_consistency', percent: 50, message: 'Scoring brand consistency...', estimatedSeconds: 10 },
  { step: 'analyzing_competitors', percent: 55, message: 'Analyzing competitors...', estimatedSeconds: 15 },
  { step: 'analyzing_technical', percent: 60, message: 'Running technical analysis...', estimatedSeconds: 10 },
  { step: 'generating_queries', percent: 65, message: 'Generating discovery queries...', estimatedSeconds: 5 },
  { step: 'running_discovery', percent: 70, message: 'Running AI discovery queries...', estimatedSeconds: 60 },
  { step: 'fetching_serp_rankings', percent: 85, message: 'Fetching search rankings...', estimatedSeconds: 15 },
  { step: 'calculating_sov', percent: 90, message: 'Calculating share of voice...', estimatedSeconds: 5 },
  { step: 'generating_recommendations', percent: 95, message: 'Generating recommendations...', estimatedSeconds: 5 },
  { step: 'finalizing', percent: 98, message: 'Finalizing report...', estimatedSeconds: 3 },
  { step: 'complete', percent: 100, message: 'Report complete!', estimatedSeconds: 0 },
];

/**
 * Map for quick lookup of step configuration
 */
export const PROGRESS_STEP_MAP: Record<ReportProgressStep, ProgressStepConfig> = 
  PROGRESS_STEPS.reduce((acc, config) => {
    acc[config.step] = config;
    return acc;
  }, {} as Record<ReportProgressStep, ProgressStepConfig>);

/**
 * Get progress configuration for a step
 */
export function getProgressConfig(step: ReportProgressStep): ProgressStepConfig {
  return PROGRESS_STEP_MAP[step] || PROGRESS_STEP_MAP['initializing'];
}

/**
 * Calculate estimated time remaining based on current step
 */
export function getEstimatedTimeRemaining(currentStep: ReportProgressStep): number {
  const currentIndex = PROGRESS_STEPS.findIndex(s => s.step === currentStep);
  if (currentIndex === -1) return 0;
  
  // Sum up estimated seconds for remaining steps
  return PROGRESS_STEPS
    .slice(currentIndex)
    .reduce((total, step) => total + step.estimatedSeconds, 0);
}

/**
 * Get all steps that come after the current step (for showing what's next)
 */
export function getRemainingSteps(currentStep: ReportProgressStep): ProgressStepConfig[] {
  const currentIndex = PROGRESS_STEPS.findIndex(s => s.step === currentStep);
  if (currentIndex === -1) return [];
  return PROGRESS_STEPS.slice(currentIndex + 1);
}

/**
 * Format seconds into human-readable time string
 */
export function formatTimeRemaining(seconds: number): string {
  if (seconds <= 0) return 'Almost done';
  if (seconds < 60) return `About ${Math.ceil(seconds / 10) * 10} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return minutes === 1 ? 'About 1 minute' : `About ${minutes} minutes`;
}
